'use client';

import { useState, useEffect, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { type Locale, resolveLandingLocale } from '@/i18n/config';

type TranslationMessages = {
  [key: string]: any;
};

/**
 * Hook to use translations on the landing page (outside [locale] routes)
 * Loads translations based on URL (when present) and localStorage preference.
 * Always resolves locale inside effects/handlers so `languageChange` sees fresh storage.
 */
export function useLandingTranslations() {
  const [messages, setMessages] = useState<TranslationMessages>({});
  const [locale, setLocale] = useState<Locale>('pt-BR');
  const [isLoading, setIsLoading] = useState(true);
  const pathname = usePathname();

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    async function loadTranslations() {
      const loc = resolveLandingLocale(pathname);
      try {
        if (!cancelled) setLocale(loc);
        const translationModule = await import(`../../messages/${loc}.json`);
        if (!cancelled) {
          setMessages(translationModule.default || translationModule);
        }
      } catch (error) {
        console.error('Failed to load translations:', error);
        if (!cancelled) setMessages({});
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void loadTranslations();

    function reloadWithoutLoadingSpinner() {
      const loc = resolveLandingLocale(pathname);
      void (async () => {
        try {
          if (!cancelled) setLocale(loc);
          const translationModule = await import(`../../messages/${loc}.json`);
          if (!cancelled) {
            setMessages(translationModule.default || translationModule);
          }
        } catch (error) {
          console.error('Failed to load translations:', error);
        }
      })();
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', reloadWithoutLoadingSpinner);
      window.addEventListener('languageChange', reloadWithoutLoadingSpinner);

      return () => {
        cancelled = true;
        window.removeEventListener('storage', reloadWithoutLoadingSpinner);
        window.removeEventListener('languageChange', reloadWithoutLoadingSpinner);
      };
    }

    return () => {
      cancelled = true;
    };
  }, [pathname]);

  // Helper function to get nested translation
  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const keys = key.split('.');
      let value: any = messages;

      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          return key; // Return key if translation not found
        }
      }

      // Handle string interpolation
      if (typeof value === 'string' && params) {
        return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
          return params[paramKey]?.toString() || match;
        });
      }

      return typeof value === 'string' ? value : key;
    },
    [messages],
  );

  return { t, locale, isLoading };
}
